import { useSignal } from "@preact/signals";
import { useState } from "preact/hooks"; // Added import for useState
import { SignInForm } from "../../components/auth/SignInForm.tsx";
import { SignUpForm } from "../../components/auth/SignUpForm.tsx";
import { ResetPasswordRequestForm } from "@/components/auth/ResetPasswordRequestForm.tsx";

interface FormControlProps {
  initialFormType?: "signin" | "signup" | "reset-password";
  initialEmail?: string;
  initialIgn?: string;
  initialServer?: string;
  initialGame?: string; // Added initialGame prop
}

export default function FormControl({ initialFormType, initialEmail, initialIgn, initialServer, initialGame }: FormControlProps) {
  const currentForm = useSignal(initialFormType || "signin");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  const showSignIn = () => {
    currentForm.value = "signin";
    setSubmitError(null);
    setSubmitSuccess(null);
  };
  const showSignUp = () => {
    currentForm.value = "signup";
    setSubmitError(null);
    setSubmitSuccess(null);
  };
  const showResetPassword = () => {
    currentForm.value = "reset-password";
    setSubmitError(null);
    setSubmitSuccess(null);
  };

  const handleResetPasswordSubmit = async (email: string) => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    const formData = new FormData();
    formData.append("email", email);
    formData.append("formType", "reset-password");

    try {
      const response = await fetch("/login", { // POST to /login route
        method: "POST",
        body: formData,
        headers: {
          "Accept": "application/json", // Ensure backend knows to send JSON
        },
      });
      const data = await response.json();

      if (response.ok && data.message) {
        setSubmitSuccess(data.message);
        // Optionally, you could switch back to sign-in form after a delay
        // setTimeout(showSignIn, 3000); 
      } else {
        setSubmitError(data.error || "Failed to send reset link. Please try again.");
      }
    } catch (err) {
      console.error("Reset password request error:", err);
      let message = "An unexpected error occurred.";
      if (err instanceof Error) {
        message = `An unexpected error occurred: ${err.message}`;
      }
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Toggle Buttons: Only show if not on reset-password form */}
      {currentForm.value !== "reset-password" && (
        <div class="flex justify-center mb-8">
          <div class="bg-gray-900/50 p-1 rounded-2xl border border-white/5 flex gap-1 w-full max-w-xs" role="group">
            <button
              type="button"
              onClick={showSignIn}
              class={`flex-1 py-3 px-4 rounded-xl font-bold transition-all duration-300 ${
                currentForm.value === "signin" 
                  ? "bg-accent text-black shadow-lg shadow-accent/20" 
                  : "text-gray-400 hover:text-white hover:bg-white/5"
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={showSignUp}
              class={`flex-1 py-3 px-4 rounded-xl font-bold transition-all duration-300 ${
                currentForm.value === "signup" 
                  ? "bg-accent text-black shadow-lg shadow-accent/20" 
                  : "text-gray-400 hover:text-white hover:bg-white/5"
              }`}
            >
              Sign Up
            </button>
          </div>
        </div>
      )}

      {/* Sign In Form */}
      {currentForm.value === "signin" && (
        <SignInForm toggleForm={showResetPassword} initialEmail={initialEmail} />
      )}

      {/* Sign Up Form */}
      {currentForm.value === "signup" && (
        <SignUpForm
          toggleForm={showSignIn}
          initialEmail={initialEmail}
          initialIgn={initialIgn}
          initialServer={initialServer}
          initialGame={initialGame} // Pass initialGame to SignUpForm
        />
      )}

      {/* Reset Password Request Form */}
      {currentForm.value === "reset-password" && (
        <ResetPasswordRequestForm
          showSignInForm={showSignIn}
          initialEmail={initialEmail}
          onSubmit={handleResetPasswordSubmit}
          isSubmitting={isSubmitting}
          submitError={submitError}
          submitSuccess={submitSuccess}
        />
      )}
    </>
  );
}